// Email sent to the client with their formula. Built by the `sendPerfumeByEmail` Cloud Function
// (functions/src/index.ts), shared with the app. See docs/send-perfume-by-email.md
import { allAvailableIngredients } from "./AvailablePerfumeIngredients";
import { capitalizeIngredientName, ingredientDisplayName } from "./IngredientDisplayName";
import type { Language } from "./Language";
import {
  ComponentCategory,
  makeIngredient,
  perfumeUserFullname,
  perfumeVolume,
  removeEmptyComponents,
  type Perfume,
  type PerfumeComponent,
} from "./Perfume";
import { PageLink } from "./Routes";

// name of the callable Cloud Function
export const SEND_PERFUME_BY_EMAIL_FUNCTION = "sendPerfumeByEmail";
export const SEND_PERFUME_BY_EMAIL_REGION = "europe-west1";

// a perfume can be sent this many times: the function is open to anybody knowing a perfume id, it
// must not be usable to send many emails
export const PERFUME_EMAIL_MAX_SENDS = 5;

export interface SendPerfumeByEmailRequest {
  recipeId: string;
  email: string;
  language: Language;
}

export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  return trimmed.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

export interface PerfumeEmail {
  subject: string;
  text: string;
  html: string;
}

const categories = [
  ComponentCategory.NOTE_DE_TETE,
  ComponentCategory.NOTE_DE_COEUR,
  ComponentCategory.NOTE_DE_FOND,
] as const;

const emailTexts: Record<
  Language,
  {
    locale: string;
    subject: (formulaNumber: string) => string;
    greeting: (fullname: string) => string;
    intro: (date: string) => string;
    formula: (formulaNumber: string) => string;
    categories: Record<ComponentCategory, string>;
    total: (numberOfComponents: number, volume: number) => string;
    link: string;
    signature: string;
  }
> = {
  fr: {
    locale: "fr-FR",
    subject: (formulaNumber) => `Votre formule de parfum Galimard ${formulaNumber}`,
    greeting: (fullname) => (fullname ? `Bonjour ${fullname},` : "Bonjour,"),
    intro: (date) =>
      `Voici la formule du parfum que vous avez créé à l'atelier Galimard le ${date}.`,
    formula: (formulaNumber) => `Formule n° ${formulaNumber}`,
    categories: {
      [ComponentCategory.NOTE_DE_TETE]: "Notes de tête",
      [ComponentCategory.NOTE_DE_COEUR]: "Notes de cœur",
      [ComponentCategory.NOTE_DE_FOND]: "Notes de fond",
    },
    total: (numberOfComponents, volume) => `${numberOfComponents} composants pour ${volume} ml`,
    link: "Retrouvez votre formule et votre diplôme :",
    signature: "L'atelier de création de parfum Galimard",
  },
  en: {
    locale: "en-GB",
    subject: (formulaNumber) => `Your Galimard perfume formula ${formulaNumber}`,
    greeting: (fullname) => (fullname ? `Hello ${fullname},` : "Hello,"),
    intro: (date) =>
      `Here is the formula of the perfume you created at the Galimard workshop on ${date}.`,
    formula: (formulaNumber) => `Formula number ${formulaNumber}`,
    categories: {
      [ComponentCategory.NOTE_DE_TETE]: "Head notes",
      [ComponentCategory.NOTE_DE_COEUR]: "Heart notes",
      [ComponentCategory.NOTE_DE_FOND]: "Base notes",
    },
    total: (numberOfComponents, volume) => `${numberOfComponents} components for ${volume} ml`,
    link: "Find your formula and your diploma:",
    signature: "The Galimard perfume workshop",
  },
};

function componentName(component: PerfumeComponent, language: Language): string {
  const [ingredient] = makeIngredient(component, allAvailableIngredients);
  return ingredient === null
    ? capitalizeIngredientName(component.name)
    : ingredientDisplayName(ingredient, language);
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function buildPerfumeEmail({
  perfume,
  language,
  appUrl,
}: {
  perfume: Perfume;
  language: Language;
  // e.g. https://atelier-galimard.yespara.eu, never given by the client (link in the email)
  appUrl: string;
}): PerfumeEmail {
  const texts = emailTexts[language];
  const formulaNumber = perfume.formulaNumber ?? "";
  const date = new Intl.DateTimeFormat(texts.locale, {
    dateStyle: "long",
    timeZone: "Europe/Paris",
  }).format(perfume.created);
  const components = removeEmptyComponents(perfume.components);
  const sections = categories
    .map((category) => ({
      title: texts.categories[category],
      lines: components
        .filter((component) => component.category === category)
        .map((component) => `${componentName(component, language)} — ${component.volume} ml`),
    }))
    .filter((section) => section.lines.length > 0);
  const total = texts.total(components.length, perfumeVolume(perfume));
  const link = `${appUrl.replace(/\/+$/, "")}${PageLink.perfumeDetails(perfume.recipeId)}`;
  const greeting = texts.greeting(perfumeUserFullname(perfume));

  const text = [
    greeting,
    "",
    texts.intro(date),
    "",
    texts.formula(formulaNumber),
    ...sections.flatMap((section) => [
      "",
      section.title,
      ...section.lines.map((line) => `- ${line}`),
    ]),
    "",
    total,
    "",
    texts.link,
    link,
    "",
    texts.signature,
  ].join("\n");

  const html = [
    `<p>${escapeHtml(greeting)}</p>`,
    `<p>${escapeHtml(texts.intro(date))}</p>`,
    `<h2>${escapeHtml(texts.formula(formulaNumber))}</h2>`,
    ...sections.map(
      (section) =>
        `<h3>${escapeHtml(section.title)}</h3><ul>${section.lines
          .map((line) => `<li>${escapeHtml(line)}</li>`)
          .join("")}</ul>`,
    ),
    `<p>${escapeHtml(total)}</p>`,
    `<p>${escapeHtml(texts.link)}<br><a href="${escapeHtml(link)}">${escapeHtml(link)}</a></p>`,
    `<p>${escapeHtml(texts.signature)}</p>`,
  ].join("\n");

  return { subject: texts.subject(formulaNumber), text, html };
}
